import type { AutomationConfig } from "@smartsuite/automation-engine-api";
import { ModalOverlay } from "@smartsuite/react-ui/lib";
import { changeLanguage } from "i18next";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { getSolutionCSSColorPalette } from "../../services/colorHelper";
import { useEngineClient } from "../../services/engineClient";
import { localStorageClient } from "../../services/local-storage-client";
import { useRootStore } from "../../stores/hooks";
import type { SolutionModel } from "../../types/solution";
import * as S from "./style";
import { useLimitsClient } from "../../services/limitsClient";
import { useSolutionDirectory } from "../../services/solution-directory";
import { Routes, Navigate, Route } from "react-router-dom";
import { AutomationList } from "../AutomationList";
import { AutomationForm } from "../AutomationForm";
import { configure as configureFlashNotifications } from "@smartsuite/flash-notifications";
import type { SolutionCSSColorPalette } from "@smartsuite/types";
import type { SolutionColor } from "@smartsuite/types/lib/constants/color";
import { useAnnounceLocation } from "./use-announce-location";

export const App = observer<{
  lang: string;
  logoColor: SolutionColor;
  solution?: SolutionModel;
  onClose?: () => void;
}>(({ lang, solution, logoColor, onClose }) => {
  useAnnounceLocation();

  const onCloseModal = (): void => {
    if (localStorage.getItem("hide-my-first-automation-modal") === null) {
      localStorage.setItem("hide-my-first-automation-modal", "true");
    }
    onClose?.();
    // it's needed to prevent scroll to top before closing modal
    setTimeout(() => {
      store.automations.setScrollListState(0);
    }, 1000);
  };

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [loadingErr, setLoadingErr] = useState<string | undefined>();

  const store = useRootStore();
  const mainColor =
    solution?.logo_color !== undefined ? solution.logo_color : logoColor;
  const engineClient = useEngineClient();
  const limitsClient = useLimitsClient();
  const { loadForSolutions, loadWorkspace } = useSolutionDirectory();

  const colorPalette: SolutionCSSColorPalette =
    getSolutionCSSColorPalette(mainColor);

  store.theme.setThemeColor({
    primaryColor: mainColor,
    hoverColor: colorPalette["solution-hover-color"],
    secondaryColor: colorPalette["solution-secondary-color"],
    highlightColor: colorPalette["solution-highlight-color"],
  });

  void changeLanguage(lang);

  useEffect(() => {
    configureFlashNotifications();
  }, []);

  useEffect(() => {
    /*
    Here we run all calls in parallel, but react on each call individually
    */
    const loadInitialData = async (): Promise<void> => {
      let automationsCallResolved = false;
      let automationsCount: number;

      let applicationsCallResolved = false;

      let solutionsCall: Promise<void> | undefined;

      const checkReadyToProceed = (): void => {
        // autmations and applications are enough to proceed and render main page
        // if we have zero autoimations - its enough to proceed.
        if (
          isLoading &&
          automationsCallResolved &&
          (automationsCount === 0 || applicationsCallResolved)
        ) {
          setIsLoading(false);
        }
      };

      let automationsCall: Promise<void> | undefined;
      // try to load data from localStorage
      const data = localStorageClient.getItem<{
        solutionId: string;
        automations: AutomationConfig[];
        nextPageToken: string;
      }>(
        "smartsuite-automations",
        (cachedData) => cachedData.solutionId === store.config.solutionId,
      );
      if (data !== undefined) {
        store.automations.appendPage(data.automations, data.nextPageToken);
        automationsCount = data.automations.length;
        // The warmup caches only the first page. When more pages exist, keep
        // loading from its token below, otherwise the list shows only the
        // first page and per-table counts are too low (BUG-22135).
        if (data.nextPageToken === "") {
          automationsCall = Promise.resolve();
          automationsCallResolved = true;
          store.automations.setAutomationsCallResolved(true);
          checkReadyToProceed();
        }
      }

      void limitsClient.getLimits().then((limits) => {
        store.limits.setLimits(limits);
      });

      if (!automationsCallResolved) {
        getAutomation(data?.nextPageToken ?? "");

        function getAutomation(nextPageToken: string): void {
          automationsCall = engineClient
            .getAutomationList(
              store.config.solutionId,
              store.automations.pageSize,
              nextPageToken,
            )
            .then((automations) => {
              store.automations.appendPage(
                automations.automations,
                automations.nextPageToken,
              );
              automationsCount = automations.automations.length;

              if (automations.nextPageToken !== "") {
                getAutomation(automations.nextPageToken);
              } else {
                automationsCallResolved = true;
                store.automations.setAutomationsCallResolved(true);
                checkReadyToProceed();
              }
            });
        }
      }

      // The host app passes a one-time snapshot of its client-side table
      // store. That snapshot can be partial (tables not loaded yet) or stale
      // (tables renamed/replaced since the page was loaded), which made the
      // per-table automation counts differ between users (BUG-22135). Use it
      // to render immediately, but always refresh from the engine, which
      // reads the tables from the platform.
      const hasApplicationsSnapshot = store.applications.list.length > 0;
      applicationsCallResolved = hasApplicationsSnapshot;
      const applicationsCall = engineClient
        .getApplications(store.config.solutionId)
        .then((applications) => {
          store.applications.setList(applications);
          applicationsCallResolved = true;
          checkReadyToProceed();
        })
        .catch((err) => {
          // With a snapshot the list is still usable; without one the
          // failure must surface like any other initial-load error.
          if (!hasApplicationsSnapshot) {
            throw err;
          }
        });

      if (store.solutions.list.length === 0) {
        solutionsCall = engineClient.getSolutions({}).then((solutions) => {
          store.solutions.setList(
            solutions.solutions
              .sort((a, b) =>
                a.name.localeCompare(b.name, undefined, {
                  numeric: true,
                  sensitivity: "base",
                }),
              )
              .map((solution) => ({
                id: solution.id,
                name: solution.name,
                slug: solution.slug,
                logo_icon: solution.logoIcon,
                logo_color: solution.logoColor as SolutionColor,
              })),
          );
        });
      } else {
        solutionsCall = Promise.resolve();
      }

      const integrationsCall = engineClient
        .getIntegrationsList(store.integrations.pageSize, "")
        .then((integrations) => {
          store.integrations.setList(integrations);
          checkReadyToProceed();
        });

      const credentialsCall = engineClient
        .getAllCredentialList({
          solutionId: store.config.solutionId,
          pageToken: "",
          pageSize: store.credentials.pageSize,
        })
        .then((credentials) => {
          store.credentials.setList(credentials);
          store.credentials.setLoaded(true);
          checkReadyToProceed();
        });

      const teamsAndMembers = Promise.all([
        loadForSolutions([store.config.solutionId]),
        loadWorkspace(),
      ]).then(() => {
        checkReadyToProceed();
      });

      await Promise.all([
        automationsCall,
        applicationsCall,
        integrationsCall,
        credentialsCall,
        teamsAndMembers,
        solutionsCall,
      ])
        .catch((err) => {
          if (err instanceof Error) {
            setLoadingErr(err.message);
          } else {
            setLoadingErr("Failed to load data");
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    };

    setIsLoading(true);

    void loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (localStorage.getItem("showOnOffModal") === null) {
      localStorage.setItem("showOnOffModal", "true");
    }
  }, []);

  return (
    <ModalOverlay animation="none" active={true} closeByEsc={false}>
      <S.LocalCssVariablesWrapper>
        <Routes>
          <Route path="/">
            <Route index element={<Navigate to="list" />} />
            <Route
              path="list"
              element={
                <AutomationList
                  onClose={onCloseModal}
                  isLoading={isLoading}
                  loadingErr={loadingErr}
                />
              }
            />
            <Route
              path={":id"}
              element={<AutomationForm onClose={onCloseModal} />}
            />
            <Route
              path={":id/history"}
              element={
                <AutomationForm
                  onClose={onCloseModal}
                  currSection={{ type: "history", hash: "" }}
                />
              }
            />
            <Route
              path={":id/history/:historyId"}
              element={
                <AutomationForm
                  onClose={onCloseModal}
                  currSection={{ type: "history", hash: "" }}
                />
              }
            />
            <Route
              path={"create"}
              element={<AutomationForm onClose={onCloseModal} />}
            />
          </Route>
        </Routes>
      </S.LocalCssVariablesWrapper>
    </ModalOverlay>
  );
});
